import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import {
  Button,
  Text,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import { 
  Dismiss24Regular,
  Play20Regular,
} from '@fluentui/react-icons';
import { StatusType } from '../StatusBadge';
import { TenantPolicyTableStyles } from '../Styles';
import { sortByDeploymentPercent } from '../common/utils';
import { 
  PolicyTableContainer,
  PolicyTable,
  EntityFilterBar,
  TenantPolicyRow,
  TenantViewDetailsDialog,
  TENANT_GRID_COLUMNS,
  TENANT_GRID_COLUMNS_NO_PRIORITY,
  PoliciesPaginationFooter,
} from './shared';
import { RuleSelectionStep, SummaryStep, TenantDeploySummary } from './tenantDeployDialog';
import { useToggleSet } from '../../hooks/useToggleSet';
import { useDialogState } from '../../hooks/useDialogState';
import { usePolicyTableState } from './usePolicyTableState';
import { useServerPagination } from './useServerPagination';
import { useSummaryRefresh } from './useSummaryRefresh';
import { useAppDispatch, useAppSelector } from '../../hooks';
import {
  fetchTenantPolicies,
  selectTenantPolicies,
  selectTenantPoliciesLoading,
  selectTenantPoliciesError,
  selectTenantPoliciesMetadata,
  updatePolicyDeploymentStatuses,
} from '../../slices/tenantPoliciesSlice';
import { buildTenantDeployRequest, batchDeploy } from '../../services/deployment.service';
import {
  TenantPolicy,
  TenantBaseRule,
  TenantTargetRule,
  entityMatchKey,
  labelMatchKey,
  labelDisplayText,
  DeploymentResult,
  TrackedDeployment,
} from './types';
import { trackDeployments, selectActiveDeploymentCount } from '../../slices/deploymentTrackingSlice';
import { selectTenantEntities, selectTenantPrefix } from '../../slices/tenantsSlice';
import TenantPrefixBox from '../common/TenantPrefixBox';
import DeploymentWarningBanner from '../common/DeploymentWarningBanner';
import { ensureDomainSuffix, isGuid, mapTabToPolicyType } from './utils';
import type { SummaryEntity } from './utils';
import { STATUS_OPTIONS } from './constants';

type Props = {
  tenantId: string;
  tenantDisplay?: string;
};

export default function TenantPoliciesTable({ tenantId, tenantDisplay }: Props) {
  const styles = TenantPolicyTableStyles();
  const dispatch = useAppDispatch();

  // Redux state
  const allPolicies = useAppSelector(selectTenantPolicies);
  const loading = useAppSelector(selectTenantPoliciesLoading);
  const error = useAppSelector(selectTenantPoliciesError);
  const metadata = useAppSelector(selectTenantPoliciesMetadata);

  // Global tenant entities for name/domain fallback
  const tenantEntities = useAppSelector(selectTenantEntities);
  const prefix = useAppSelector(s => selectTenantPrefix(s, metadata.targetTenantId || tenantId));

  // Active deployment count (polling runs at App level)
  const activeDeployCount = useAppSelector(selectActiveDeploymentCount);

  // Use shared policy table state hook
  const {
    selectedTab,
    setSelectedTab,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    filteredPolicies,
    dlpCount,
    autoLabelCount,
    resetFilters,
    deployMessage,
    setDeployMessage,
  } = usePolicyTableState({
    policies: allPolicies,
    dlpTotalCount: metadata.dlpTotalCount,
    autoLabelTotalCount: metadata.autoLabelTotalCount,
  });

  // Local page state + debounced search (snap back to page 1 when search changes).
  const { pageNumber, setPageNumber, debouncedSearch } = useServerPagination(search);
  
  // Multi-select policies state
  const [selectedPolicyIds, , setSelectedPolicyIds] = useToggleSet();
  
  // View Details Dialog state (read-only)
  const detailsDialog = useDialogState<TenantPolicy>();
  const [detailsExpandedRules, toggleDetailsRuleExpand] = useToggleSet();
  
  // Deploy dialog state (multi-policy with left/right panels)
  const [deployDialogOpen, setDeployDialogOpen] = useState(false);
  const [deployStep, setDeployStep] = useState<1 | 2>(1);
  const [deployPolicies, setDeployPolicies] = useState<TenantPolicy[]>([]);
  const [activePolicyId, setActivePolicyId] = useState<string | null>(null);
  const [policyRuleSelections, setPolicyRuleSelections] = useState<Record<string, Set<string>>>({});
  const [expandedDeployRules, toggleDeployRuleExpand, setExpandedDeployRules] = useToggleSet();
  
  // Ref for right panel to scroll to top
  const rightPanelRef = useRef<HTMLDivElement>(null);
  
  // Fetch tenant policies on mount or when tenantId / tab / page / search changes
  useEffect(() => {
    if (tenantId) {
      dispatch(
        fetchTenantPolicies({
          tenantId,
          policyType: mapTabToPolicyType(selectedTab),
          pageNumber,
          searchTerm: debouncedSearch || undefined,
        }),
      );
    }
  }, [dispatch, tenantId, selectedTab, pageNumber, debouncedSearch]);

  // Re-fetch policies when the deploy dialog reaches the summary step so the
  // "Items that will be created" box reflects the CURRENT deployment state of
  // the target tenant (a label deployed since page load must not be listed as
  // "to be created"). Existence is derived from the live Redux slice, so a
  // refetch is sufficient; getDeploySummary() recomputes automatically.
  //
  // useSummaryRefresh derives the refreshing state during render (so the first summary frame is
  // never shown as settled) and unwraps the thunk (so a failed refresh surfaces instead of
  // silently enabling Deploy over unrevalidated data).
  const summaryRefresh = useSummaryRefresh(
    deployDialogOpen && deployStep === 2 && !!tenantId,
    useCallback(
      () =>
        dispatch(
          fetchTenantPolicies({
            tenantId: tenantId!,
            policyType: mapTabToPolicyType(selectedTab),
            pageNumber,
            searchTerm: debouncedSearch || undefined,
          }),
        ).unwrap(),
      [dispatch, tenantId, selectedTab, pageNumber, debouncedSearch],
    ),
  );

  const headerDisplay = useMemo(() => {
    return tenantDisplay || metadata.targetTenantName || tenantId || '';
  }, [tenantDisplay, tenantId, metadata.targetTenantName]);

  // Resolved target tenant domain (with .onmicrosoft.com suffix) for policy dialogs
  const resolvedTargetName = useMemo(() => {
    // 1. Try API metadata name (skip if it's a GUID)
    const apiName = metadata.targetTenantName?.trim();
    if (apiName && !isGuid(apiName)) return ensureDomainSuffix(apiName);

    // 2. Try global tenants store (authorized tenants)
    const globalName = tenantEntities[metadata.targetTenantId || tenantId]?.name?.trim();
    if (globalName) return ensureDomainSuffix(globalName);

    // 3. Fall back to raw tenant ID
    return tenantId;
  }, [metadata.targetTenantName, metadata.targetTenantId, tenantId, tenantEntities]);

  // Resolved base tenant name (with .onmicrosoft.com suffix)
  const resolvedBaseName = useMemo(() => {
    return metadata.baseTenantName ? ensureDomainSuffix(metadata.baseTenantName) : 'Base Tenant';
  }, [metadata.baseTenantName]);

  // Handle policy selection
  const handlePolicyToggle = (policyId: string, checked: boolean) => {
    setSelectedPolicyIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(policyId);
      } else {
        newSet.delete(policyId);
      }
      return newSet;
    });
  };

  // Handle Select All
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedPolicyIds(new Set(filteredPolicies.map(p => p.policyId)));
    } else {
      setSelectedPolicyIds(new Set());
    }
  };

  const allSelected = filteredPolicies.length > 0 && selectedPolicyIds.size === filteredPolicies.length;
  const someSelected = selectedPolicyIds.size > 0 && selectedPolicyIds.size < filteredPolicies.length;

  // Handle View Details - read-only mode (no checkboxes, no deploy)
  const handleViewDetails = (policy: TenantPolicy) => {
    detailsDialog.open(policy);
  };

  const handleCloseDetailsDialog = () => {
    detailsDialog.close();
  };

  // Handle Deploy button (for multi-select from top) - opens deploy dialog
  const handleDeploySelected = () => {
    if (selectedPolicyIds.size === 0) return;
    
    // Get all selected policies
    const policies = allPolicies.filter(p => selectedPolicyIds.has(p.policyId));
    setDeployPolicies(policies);
    
    // Initialize rule selections for all policies (all base rules selected by default)
    const initialSelections: Record<string, Set<string>> = {};
    policies.forEach(policy => {
      const allRuleIds = new Set<string>();
      policy.baseRules.forEach(r => allRuleIds.add(r.ruleId));
      initialSelections[policy.policyId] = allRuleIds;
    });
    setPolicyRuleSelections(initialSelections);
    
    // Build deployment items and sort by deployment status
    const deploymentItems = policies.map(policy => ({
      id: policy.policyId,
      deployedCount: getDeployedRuleIds(policy.policyId).size,
      totalCount: policy.baseRules.length,
    }));
    const { firstId } = sortByDeploymentPercent(deploymentItems);
    
    // Set first policy (in sorted order) as active
    setActivePolicyId(firstId);
    setExpandedDeployRules(new Set());
    setDeployStep(1); // Start at step 1
    setDeployDialogOpen(true);
  };

  const handleCloseDeployDialog = () => {
    setDeployDialogOpen(false);
    setDeployPolicies([]);
    setActivePolicyId(null);
    setPolicyRuleSelections({});
    setExpandedDeployRules(new Set());
    setDeployStep(1);
  };

  // Handle clicking on a policy in the left panel - scroll right panel to top
  const handlePolicyClick = useCallback((policyId: string) => {
    setActivePolicyId(policyId);
    setExpandedDeployRules(new Set());
    // Scroll right panel to top
    if (rightPanelRef.current) {
      rightPanelRef.current.scrollTop = 0;
    }
  }, []);

  // Handle rule selection in deploy dialog
  const handleRuleToggle = (policyId: string, ruleId: string, checked: boolean) => {
    setPolicyRuleSelections(prev => {
      const newSelections = { ...prev };
      const currentSet = new Set(prev[policyId] || []);
      if (checked) {
        currentSet.add(ruleId);
      } else {
        currentSet.delete(ruleId);
      }
      newSelections[policyId] = currentSet;
      return newSelections;
    });
  };

  // Get policies with no rules selected (for validation)
  const getPoliciesWithNoRules = useCallback(() => {
    return deployPolicies.filter(policy => {
      const selectedRules = policyRuleSelections[policy.policyId];
      return !selectedRules || selectedRules.size === 0;
    });
  }, [deployPolicies, policyRuleSelections]);

  // Deploy using BFF deployment API
  const handleConfirmDeploy = async () => {
    // Validate all policies have at least 1 rule selected
    const policiesWithNoRules = getPoliciesWithNoRules();
    if (policiesWithNoRules.length > 0) {
      return; // Don't deploy if validation fails
    }
    
    try {
      // Build BFF deployment request from deploy dialog state
      const request = buildTenantDeployRequest(
        deployPolicies,
        policyRuleSelections,
        metadata.targetTenantId || tenantId,
      );

      if (request.entities.length === 0) {
        setDeployMessage({ intent: 'warning', title: 'No Items', body: 'No deployment items to submit.' });
        return;
      }

      // Call the BFF deploy API
      const result: DeploymentResult = await batchDeploy(request);

      // Update local Redux state with new statuses
      if (result.statusUpdates.size > 0) {
        dispatch(updatePolicyDeploymentStatuses(Array.from(result.statusUpdates.entries())));
      }

      // Register accepted items for deployment status polling
      if (result.acceptedItems.length > 0) {
        const now = Date.now();
        const trackedItems: TrackedDeployment[] = result.acceptedItems.map(item => {
          const matchedPolicy = deployPolicies.find(
            p => p.policyName.toLowerCase() === item.entityId.toLowerCase()
          );
          return {
            deploymentId: item.deploymentId,
            tenantId: item.tenantId,
            entityId: item.entityId,
            policyName: matchedPolicy?.policyName || item.entityId,
            view: 'tenant' as const,
            apiStatus: 'Received',
            startedAt: now,
            lastPolledAt: now,
          };
        });
        dispatch(trackDeployments(trackedItems));
      }

      // Show message bar
      if (result.success && result.failedCount === 0) {
        setDeployMessage({ intent: 'success', title: 'Deployment Initiated', body: result.message });
      } else if (result.success) {
        setDeployMessage({ intent: 'warning', title: 'Partial Deployment', body: result.message });
      } else {
        setDeployMessage({ intent: 'error', title: 'Deployment Failed', body: result.message });
      }

      // Close dialog and refresh
      handleCloseDeployDialog();
      setSelectedPolicyIds(new Set());
      dispatch(
        fetchTenantPolicies({
          tenantId,
          policyType: mapTabToPolicyType(selectedTab),
          pageNumber,
          searchTerm: debouncedSearch || undefined,
        }),
      );
    } catch (err) {
      console.error('Deployment failed:', err);
      setDeployMessage({
        intent: 'error',
        title: 'Deployment Error',
        body: err instanceof Error ? err.message : 'An unexpected error occurred.',
      });
    }
  };

  // Get policy status from deployment status
  const getPolicyStatus = (policy: TenantPolicy): StatusType => {
    if (policy.status === 'Deployed') return 'Available';
    if (policy.status === 'PartiallyDeployed') return 'Partially Deployed';
    if (policy.status === 'InProgress') return 'In Progress';
    if (policy.status === 'DeploymentFailed') return 'Failed';
    return 'Not Available';
  };

  // Get deployed rule IDs for a policy (using isDeployed flag from baseRules)
  const getDeployedRuleIds = useCallback((policyId: string) => {
    const policy = allPolicies.find(p => p.policyId === policyId);
    if (!policy || !policy.baseRules) return new Set<string>();
    const deployedFromBase = policy.baseRules.filter(r => r.isDeployed).map(r => r.ruleId);
    const deployedFromTarget = (policy.targetRules || []).map(r => r.ruleId);
    return new Set([...deployedFromBase, ...deployedFromTarget]);
  }, [allPolicies]);

  // Get all existing SITs and Labels using isDeployed flag from API responses
  const getExistingSitsAndLabels = useCallback(() => {
    const existingSits = new Set<string>();
    const existingLabels = new Set<string>();
    
    // Go through all policies' target and base rules, using isDeployed flag
    allPolicies.forEach(policy => {
      // Target rules: SITs/Labels with isDeployed=true are confirmed existing
      if (policy.targetRules) {
        policy.targetRules.forEach(rule => {
          (rule.sits || []).filter(s => s.isDeployed ?? false).forEach(s => {
            if (s.name) existingSits.add(entityMatchKey(s.name));
          });
          (rule.labels || []).filter(l => l.isDeployed ?? false).forEach(l => {
            const key = labelMatchKey(l);
            if (key) existingLabels.add(key);
          });
        });
      }
      
      // Base rules: SITs/Labels with isDeployed=true are also confirmed existing
      if (policy.baseRules) {
        policy.baseRules.forEach(rule => {
          (rule.sits || []).filter(s => s.isDeployed ?? false).forEach(s => {
            if (s.name) existingSits.add(entityMatchKey(s.name));
          });
          (rule.labels || []).filter(l => l.isDeployed ?? false).forEach(l => {
            const key = labelMatchKey(l);
            if (key) existingLabels.add(key);
          });
        });
      }
    });
    
    return { sits: existingSits, labels: existingLabels };
  }, [allPolicies]);

  // Calculate summary for Step 2
  const getDeploySummary = useCallback((): TenantDeploySummary => {
    const { sits: existingSits, labels: existingLabels } = getExistingSitsAndLabels();
    
    const selectedRules: { policy: TenantPolicy; rules: (TenantBaseRule | TenantTargetRule)[] }[] = [];
    // Keyed by match key (lower-cased internal name) -> display text, so the existence
    // comparison matches the backend while the summary still shows friendly names.
    const allSits = new Map<string, string>();
    const allLabels = new Map<string, string>();
    
    deployPolicies.forEach(policy => {
      const selectedRuleIds = policyRuleSelections[policy.policyId] || new Set();
      const rules = policy.baseRules.filter(r => selectedRuleIds.has(r.ruleId));
      if (rules.length > 0) {
        selectedRules.push({ policy, rules });
        rules.forEach(rule => {
          (rule.sits || []).forEach(sit => {
            const key = entityMatchKey(sit.name);
            if (key) allSits.set(key, sit.name!);
          });
          (rule.labels || []).forEach(label => {
            const key = labelMatchKey(label);
            if (key) allLabels.set(key, labelDisplayText(label));
          });
        });
      }
    });
    
    // Carry both the match key (used for comparison and as the React key) and the display text.
    const sitsExisting: SummaryEntity[] = [];
    const sitsNew: SummaryEntity[] = [];
    allSits.forEach((display, key) => {
      (existingSits.has(key) ? sitsExisting : sitsNew).push({ key, display });
    });

    const labelsExisting: SummaryEntity[] = [];
    const labelsNew: SummaryEntity[] = [];
    allLabels.forEach((display, key) => {
      (existingLabels.has(key) ? labelsExisting : labelsNew).push({ key, display });
    });
    
    return {
      selectedRules,
      totalRulesCount: selectedRules.reduce((acc, p) => acc + p.rules.length, 0),
      sitsExisting,
      sitsNew,
      labelsExisting,
      labelsNew,
    };
  }, [deployPolicies, policyRuleSelections, getExistingSitsAndLabels]);

  return (
    <PolicyTableContainer
      title={headerDisplay ? `Policies for ${headerDisplay}` : 'Policies'}
      subtitle="Manage policies for this tenant"
      prefix={prefix}
      selectedTab={selectedTab}
      onTabChange={(tab) => {
        setSelectedTab(tab);
        setSearch('');
        resetFilters();
        setSelectedPolicyIds(new Set());
        setPageNumber(1);
      }}
      dlpCount={dlpCount}
      autoLabelCount={autoLabelCount}
      error={error}
    >
      {/* Prefix redeploy warnings — non-blocking, dismissible (shared across views) */}
      <DeploymentWarningBanner tenantId={metadata.targetTenantId || tenantId} />

      {/* Deployment result message bar */}
      {deployMessage && (
        <MessageBar
          intent={deployMessage.intent}
          style={{ marginBottom: 12 }}
        >
          <MessageBarBody>
            <MessageBarTitle>{deployMessage.title}</MessageBarTitle>
            {deployMessage.body}
          </MessageBarBody>
          <Button
            appearance="subtle"
            size="small"
            icon={<Dismiss24Regular />}
            onClick={() => setDeployMessage(null)}
            aria-label="dismiss message"
          />
        </MessageBar>
      )}

      {/* Active deployment polling indicator */}
      {activeDeployCount > 0 && (
        <MessageBar
          intent="info"
          style={{ marginBottom: 12 }}
        >
          <MessageBarBody>
            <MessageBarTitle>Deployment In Progress</MessageBarTitle>
            {activeDeployCount} deployment{activeDeployCount > 1 ? 's' : ''} in progress. Status updates automatically every 15 seconds.
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Search bar, status filter, and Deploy button */}
      <EntityFilterBar
        search={search}
        onSearchChange={setSearch}
        filterOptions={STATUS_OPTIONS.filter(o => o.key === 'All' || o.key === 'Deployed' || o.key === 'NotDeployed' || o.key === 'PartiallyDeployed') as unknown as { key: string; label: string }[]}
        filterValue={statusFilter}
        onFilterChange={(v) => { setStatusFilter(v); setPageNumber(1); }}
        filterAriaLabel="Filter by status"
        searchAriaLabel="Search policies"
        searchPlaceholder="Search policies..."
        className={styles.solidField}
      >
        <div style={{ flex: 1 }} />
        <Button
          appearance="primary"
          icon={<Play20Regular />}
          disabled={selectedPolicyIds.size === 0}
          onClick={handleDeploySelected}
        >
          Compare and Deploy ({selectedPolicyIds.size})
        </Button>
      </EntityFilterBar>

      {/* Table */}
      <PolicyTable
        columns={[
          { key: 'select', label: '', width: '48px' },
          { key: 'name', label: 'Policy Name', width: 'minmax(250px,2.5fr)' },
          ...(selectedTab === 'DLP' ? [{ key: 'priority', label: 'Priority', width: 'minmax(100px,0.8fr)' }] : []),
          { key: 'status', label: 'Status', width: 'minmax(140px,1.2fr)' },
          { key: 'lastDeployed', label: 'Last Deployed Time', width: 'minmax(180px,1.5fr)', isTimeColumn: true },
          { key: 'actions', label: 'Actions', width: 'minmax(120px,1fr)', align: 'center' },
        ]}
        gridTemplateColumns={selectedTab === 'DLP' ? TENANT_GRID_COLUMNS : TENANT_GRID_COLUMNS_NO_PRIORITY}
        loading={loading}
        loadingMessage="Loading policies..."
        isEmpty={filteredPolicies.length === 0}
        selectedTab={selectedTab}
        selectAllState={{
          checked: allSelected ? true : someSelected ? 'mixed' : false,
          onChange: handleSelectAll,
        }}
      >
        {filteredPolicies.map((policy: TenantPolicy, i: number) => (
          <TenantPolicyRow
            key={policy.policyId}
            policy={policy}
            gridColumns={selectedTab === 'DLP' ? TENANT_GRID_COLUMNS : TENANT_GRID_COLUMNS_NO_PRIORITY}
            showPriority={selectedTab === 'DLP'}
            isSelected={selectedPolicyIds.has(policy.policyId)}
            isLastRow={i === filteredPolicies.length - 1}
            status={getPolicyStatus(policy)}
            onToggleSelect={(checked) => handlePolicyToggle(policy.policyId, checked)}
            onViewDetails={() => handleViewDetails(policy)}
          />
        ))}
      </PolicyTable>

      {/* Pagination footer */}
      <PoliciesPaginationFooter
        pageNumber={metadata.pageNumber}
        totalPages={metadata.totalPages}
        pageSize={metadata.pageSize}
        hasNextPage={metadata.hasNextPage}
        loading={loading}
        onPageChange={setPageNumber}
      />

      {/* View Details Dialog */}
      <TenantViewDetailsDialog
        isOpen={detailsDialog.isOpen}
        policy={detailsDialog.data}
        baseTenantName={resolvedBaseName}
        targetTenantName={resolvedTargetName}
        expandedRuleIds={detailsExpandedRules}
        onToggleRuleExpand={toggleDetailsRuleExpand}
        onClose={handleCloseDetailsDialog}
      />

      {/* Deploy Dialog - 2-Step Flow */}
      <Dialog open={deployDialogOpen} onOpenChange={(_, data) => setDeployDialogOpen(data.open)}>
        <DialogSurface style={{ maxWidth: '1100px', maxHeight: '85vh', width: '90vw' }}>
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  aria-label="Close dialog"
                  title="Close dialog"
                  icon={<Dismiss24Regular />}
                  onClick={handleCloseDeployDialog}
                />
              }
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                Deploy Policies to {resolvedTargetName}
                <TenantPrefixBox prefix={prefix} />
                <Text style={{ fontSize: 12, color: '#6b7280' }}>
                  Step {deployStep} of 2
                </Text>
              </span>
            </DialogTitle>
            <DialogContent style={{ padding: 0, display: 'flex', flexDirection: 'column', height: 'clamp(300px, 65vh, 800px)' }}>
              
              {/* Step 1: Rule Selection */}
              {deployStep === 1 && (
                <RuleSelectionStep
                  deployPolicies={deployPolicies}
                  activePolicyId={activePolicyId}
                  policyRuleSelections={policyRuleSelections}
                  targetTenantName={resolvedTargetName}
                  prefix={prefix}
                  getDeployedRuleIds={getDeployedRuleIds}
                  onPolicyClick={handlePolicyClick}
                  onRuleToggle={handleRuleToggle}
                  onToggleRuleExpand={toggleDeployRuleExpand}
                  expandedRuleIds={expandedDeployRules}
                  getPoliciesWithNoRules={getPoliciesWithNoRules}
                  onNext={() => setDeployStep(2)}
                  onClose={handleCloseDeployDialog}
                  rightPanelRef={rightPanelRef}
                />
              )}
              
              {/* Step 2: Summary */}
              {deployStep === 2 && (
                <SummaryStep
                  summary={getDeploySummary()}
                  targetTenantName={resolvedTargetName}
                  prefix={prefix}
                  isRefreshing={summaryRefresh.isRefreshing}
                  refreshError={summaryRefresh.error}
                  onRetryRefresh={summaryRefresh.retry}
                  onBack={() => setDeployStep(1)}
                  onConfirm={handleConfirmDeploy}
                  onClose={handleCloseDeployDialog}
                />
              )}
              
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </PolicyTableContainer>
  );
}
